import { useMemo } from "react";
import PiBarchartDefaultStroke from "@markeroo/icons/src/stroke/chart-&-graph/PiBarchartDefaultStroke";
import { useDocumentEditMetrics } from "@/api/hooks";
import type { EditMetricsResponse } from "@/api/client";
import { ui } from "@/strings";
import { Pill, type PillVariant } from "@/components/Pill";
import {
  EDIT_METRICS_THRESHOLDS,
  RISK_LABELS,
  type RiskLevel,
} from "./editMetricsConstants";

// Re-export for backwards compatibility
export type { RiskLevel } from "./editMetricsConstants";

/**
 * Map risk levels to Pill variants for consistent styling.
 */
const RISK_TO_PILL_VARIANT: Record<RiskLevel, PillVariant> = {
  high: "error",
  medium: "warning",
  low: "success",
  none: "info",
};

interface EditMetricsIndicatorProps {
  assignmentId: string;
  documentId: string;
  /** Optional classId to enable View Edit Replay button in modal */
  classId?: string;
  /** Callback when the pill is clicked - used for URL-based modal routing */
  onClick?: () => void;
}

/**
 * Calculate risk level based on edit metrics using heuristic thresholds.
 *
 * Factors considered:
 * - Paste ratio: High paste ratio suggests copied content
 * - Error correction rate: Very low rate suggests pre-written content
 * - WPM: Unusually high WPM may indicate pasted content
 * - Total events: Very few events for a long document is suspicious
 */
function calculateRiskLevel(metrics: EditMetricsResponse): RiskLevel {
  const { paste, editBehavior, typing, totalEvents, finalWordCount } = metrics;
  const T = EDIT_METRICS_THRESHOLDS;

  // No events = no data to analyze
  if (totalEvents === 0) {
    return "none";
  }

  let riskScore = 0;

  // Paste ratio check
  if (paste.pasteToTypeRatio !== null) {
    if (paste.pasteToTypeRatio > T.PASTE_RATIO_VERY_HIGH) {
      riskScore += 3; // Very high paste ratio
    } else if (paste.pasteToTypeRatio > T.PASTE_RATIO_HIGH) {
      riskScore += 2; // High paste ratio
    } else if (paste.pasteToTypeRatio > T.PASTE_RATIO_MODERATE) {
      riskScore += 1; // Moderate paste ratio
    }
  }

  // Error correction rate check - very low rate suggests pre-written content
  if (
    editBehavior.errorCorrectionRate !== null &&
    editBehavior.insertCount > T.ERROR_CORRECTION_MIN_INSERTS
  ) {
    if (editBehavior.errorCorrectionRate < T.ERROR_CORRECTION_VERY_LOW) {
      riskScore += 2; // Suspiciously low error correction
    } else if (editBehavior.errorCorrectionRate < T.ERROR_CORRECTION_LOW) {
      riskScore += 1; // Low error correction
    }
  }

  // WPM check (unusually high WPM suggests pasting)
  if (typing.wordsPerMinute !== null) {
    if (typing.wordsPerMinute > T.WPM_VERY_HIGH) {
      riskScore += 2; // Very high WPM
    } else if (typing.wordsPerMinute > T.WPM_HIGH) {
      riskScore += 1; // High WPM
    }
  }

  // Events per word check (very few events for content length is suspicious)
  // Guard against division by zero - finalWordCount must be positive
  if (
    finalWordCount > 0 &&
    finalWordCount >= T.MIN_WORD_COUNT_FOR_EVENTS_CHECK
  ) {
    const eventsPerWord = totalEvents / finalWordCount;
    if (eventsPerWord < T.EVENTS_PER_WORD_VERY_LOW) {
      riskScore += 2; // Very few edits per word
    } else if (eventsPerWord < T.EVENTS_PER_WORD_LOW) {
      riskScore += 1; // Few edits per word
    }
  }

  // Large paste check
  if (paste.largestPasteSize > T.PASTE_SIZE_VERY_LARGE) {
    riskScore += 2; // Very large single paste
  } else if (paste.largestPasteSize > T.PASTE_SIZE_LARGE) {
    riskScore += 1; // Large single paste
  }

  // Convert score to risk level
  if (riskScore >= T.RISK_SCORE_HIGH) {
    return "high";
  } else if (riskScore >= T.RISK_SCORE_MEDIUM) {
    return "medium";
  }
  return "low";
}

function getRiskLabel(level: RiskLevel): string {
  return RISK_LABELS[level];
}

function getRiskVariant(level: RiskLevel): PillVariant {
  return RISK_TO_PILL_VARIANT[level];
}

export function EditMetricsIndicator({
  assignmentId,
  documentId,
  onClick,
}: EditMetricsIndicatorProps) {
  const {
    data: metrics,
    isLoading,
    isError,
  } = useDocumentEditMetrics(assignmentId, documentId);

  // Memoize risk calculation to avoid recalculating on every render
  const riskLevel = useMemo(
    () => (metrics ? calculateRiskLevel(metrics) : "none"),
    [metrics],
  );

  // Don't show anything while loading
  if (isLoading) {
    return null;
  }

  // Hide pill entirely when no data available
  if (isError || !metrics) {
    return null;
  }

  // Hide pill when there's no meaningful edit data (risk level "none" means totalEvents === 0)
  if (riskLevel === "none") {
    return null;
  }

  const variant = getRiskVariant(riskLevel);
  const label = getRiskLabel(riskLevel);

  return (
    <Pill
      variant={variant}
      icon={<PiBarchartDefaultStroke size={14} aria-hidden="true" />}
      onClick={onClick}
      title={ui.editMetrics.viewDetails}
      aria-label={`${label} - ${ui.editMetrics.viewDetails}`}
      aria-haspopup="dialog"
    >
      <span className="hidden sm:inline">{label}</span>
    </Pill>
  );
}

// Export the calculation function for use by parent components
export { calculateRiskLevel };
